import { useState, useEffect } from 'react';
import { MdPix, MdRestaurant, MdCreditCard } from 'react-icons/md';
import { FaCreditCard, FaMoneyBillWave, FaCheckCircle } from 'react-icons/fa';
import { BsCreditCard2FrontFill } from 'react-icons/bs';
import { AiOutlineClose, AiOutlineLoading } from 'react-icons/ai';
import { IoWarningOutline, IoInformationCircleOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import ModalPortal from '../../../components/lib/modalPortal';
import PaymentService from '../../../rest/paymentsService';
import { formatCurrency } from '../../../utils/CurrencyUtils';
import { formatString } from '../../../utils/StringUtils';

/* ─── Constants ────────────────────────────────────────────────── */

const CARD_BRANDS = ['Visa', 'Mastercard', 'Elo', 'Amex', 'Outro'];

const ALL_METHODS = [
    { key: 'MONEY',       label: 'Dinheiro',       Icon: FaMoneyBillWave,        color: 'bg-green-600'  },
    { key: 'CREDIT',      label: 'Cartão Crédito', Icon: FaCreditCard,           color: 'bg-indigo-600' },
    { key: 'DEBIT',       label: 'Cartão Débito',  Icon: BsCreditCard2FrontFill, color: 'bg-blue-600'   },
    { key: 'PIX',         label: 'PIX',            Icon: MdPix,                  color: 'bg-teal-600'   },
    { key: 'FOODZCREDIT', label: 'Crédito Foodz',  Icon: MdRestaurant,           color: 'bg-red-500'    },
];

/* ─── Helpers ──────────────────────────────────────────────────── */

/** Normalise raw DB payment method string to backend enum key */
const normalizeMethod = (raw) => {
    const u = (raw || '').toUpperCase().trim();
    if (u.includes('FOODZ') || u === 'FOODZCREDIT')                  return 'FOODZCREDIT';
    if (u === 'PIX')                                                   return 'PIX';
    if (u.includes('DÉBIT') || u === 'DEBITO'  || u === 'DEBIT')     return 'DEBIT';
    if (u.includes('CRÉDIT') || u === 'CREDITO' || u === 'CREDIT')   return 'CREDIT';
    if (u === 'DINHEIRO' || u === 'MONEY')                            return 'MONEY';
    return u;
};

/**
 * Available alternative methods for ORDER, mirroring backend rules:
 *   - When there's a real pending Asaas charge (transactionSource=ASAAS): same non-PIX
 *     blocked (CREDIT→CREDIT, DEBIT→DEBIT, MONEY→MONEY) — prevents self-confirming a
 *     stuck online charge without gateway proof. PIX→PIX allowed (manual confirmation).
 *   - Presencial payments deferred via "Confirmar Depois" (transactionSource=MANUAL, no
 *     Asaas charge at risk): no restriction — same method is a valid confirmation.
 */
const getAvailableMethods = (originalMethod, isAsaasPending) => {
    if (!isAsaasPending) return ALL_METHODS;
    const norm = normalizeMethod(originalMethod);
    return ALL_METHODS.filter(({ key }) => !(key === norm && norm !== 'PIX'));
};

const isCardMethodKey = (key) => key === 'CREDIT' || key === 'DEBIT';

/* ─── Component ────────────────────────────────────────────────── */

/**
 * Modal to confirm a PROCESSINGPAYMENT order.
 *
 * Flow:
 *  - Shows the original payment method prominently, pre-selected when confirmable
 *    (always for presencial/MANUAL orders; only PIX for pending Asaas charges).
 *  - "ou trocar para" divider + alternative methods grid.
 *  - Brand selector when CREDIT or DEBIT is chosen (required before confirm).
 *  - Note textarea visible only when an alternative is chosen.
 *
 * Props:
 *   isOpen    {boolean}
 *   order     {object}
 *   onClose   {function}
 *   onSuccess {function(orderId)}
 */
export const ConfirmAlternativePaymentModal = ({ isOpen, order, onClose, onSuccess }) => {
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [brand,          setBrand]          = useState(null);
    const [note,           setNote]           = useState('');
    const [loading,        setLoading]        = useState(false);

    // ── Derived values ──────────────────────────────────────────
    // TransactionDTO serialises as camelCase: paymentMethod (not payment_method)
    const originalRaw         = order?.transactions?.[0]?.paymentMethod || '';
    const originalNorm        = normalizeMethod(originalRaw);
    const isAsaasPending      = (order?.transactions?.[0]?.transactionSource || '').toUpperCase() === 'ASAAS';
    const availableMethods    = getAvailableMethods(originalRaw, isAsaasPending);
    const originalInAvailable = availableMethods.some(m => m.key === originalNorm);
    const originalMethodInfo  = ALL_METHODS.find(m => m.key === originalNorm);
    const alternatives        = availableMethods.filter(m => m.key !== originalNorm);
    const isAlternativeSelected = selectedMethod !== null && selectedMethod !== originalNorm;
    const needsBrand          = isCardMethodKey(selectedMethod);
    // The value actually sent to the API: append brand when card method is selected
    const effectiveMethod     = needsBrand && brand
        ? `${selectedMethod}/${brand}`
        : selectedMethod;
    // Confirm is enabled only when a method is chosen and (if card) a brand is also chosen
    const canConfirm          = !!selectedMethod && (!needsBrand || !!brand);

    // ── Reset on open ───────────────────────────────────────────
    useEffect(() => {
        if (!isOpen || !order) return;
        const raw   = order?.transactions?.[0]?.paymentMethod || '';
        const norm  = normalizeMethod(raw);
        const asaasPending = (order?.transactions?.[0]?.transactionSource || '').toUpperCase() === 'ASAAS';
        const avail = getAvailableMethods(raw, asaasPending);
        setSelectedMethod(avail.some(m => m.key === norm) ? norm : null);
        setBrand(null);
        setNote('');
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, order?.order_id]);

    if (!isOpen || !order) return null;

    // ── Handlers ────────────────────────────────────────────────
    const selectOriginal = () => {
        if (!originalInAvailable || loading) return;
        setSelectedMethod(originalNorm);
        setBrand(null);
        setNote('');
    };

    const selectAlternative = (key) => {
        if (loading) return;
        const next = selectedMethod === key ? null : key;
        setSelectedMethod(next);
        setBrand(null); // always reset brand when method changes
    };

    const handleConfirm = async () => {
        if (!selectedMethod) {
            toast.warning('Selecione uma forma de pagamento.');
            return;
        }
        if (needsBrand && !brand) {
            toast.warning('Selecione a bandeira do cartão.');
            return;
        }
        setLoading(true);
        try {
            await PaymentService.confirmAlternativePayment({
                entityType:       'ORDER',
                entityId:         String(order.order_id),
                clientCode:       sessionStorage.getItem('clientCode'),
                newPaymentMethod: effectiveMethod, // e.g. "CREDIT/Visa", "DEBIT/Mastercard", "MONEY"
                reasonNote:       note.trim() || null,
            });
            toast.success('Pagamento confirmado com sucesso!');
            onSuccess(order.order_id);
        } catch (err) {
            const status = err?.response?.status;
            if      (status === 409) toast.error('Este pedido já foi processado pelo gateway. Recarregue a página.');
            else if (status === 422) toast.error(err?.response?.data?.message || 'Operação não permitida para este método.');
            else if (status === 400) toast.error(err?.response?.data?.message || 'Método de pagamento inválido.');
            else                     toast.error('Erro ao confirmar pagamento. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (loading) return;
        onClose();
    };

    // ── Render ──────────────────────────────────────────────────
    return (
        <ModalPortal>
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-in zoom-in duration-200 flex flex-col max-h-[90vh]">

                    {/* ── Header ── */}
                    <div className="flex justify-between items-center px-6 pt-5 pb-4 border-b border-slate-100 shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <MdCreditCard className="text-green-600" size={20} />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-800 leading-tight">
                                    Confirmar Pagamento
                                </h3>
                                <span className="text-xs text-gray-400 font-medium">Pedido #{order.order_id}</span>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                        >
                            <AiOutlineClose size={20} />
                        </button>
                    </div>

                    {/* ── Body ── */}
                    <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">

                        {/* Order summary */}
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Resumo</span>
                                <span className="text-base font-bold text-gray-800">{formatCurrency(order.total)}</span>
                            </div>
                            <p className="text-xs text-gray-500 line-clamp-2">{formatString(order.items_list)}</p>
                        </div>

                        {/* ── Original method card ── */}
                        <div>
                            <span className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                                Método original
                            </span>

                            <button
                                type="button"
                                onClick={selectOriginal}
                                disabled={!originalInAvailable || loading}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left
                                    ${selectedMethod === originalNorm
                                        ? 'border-custom_blue bg-blue-50 shadow-sm'
                                        : originalInAvailable
                                            ? 'border-slate-200 hover:border-slate-300 bg-white'
                                            : 'border-slate-100 bg-slate-50 cursor-not-allowed'
                                    }`}
                            >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                                    ${originalMethodInfo?.color ?? 'bg-gray-400'}
                                    ${!originalInAvailable ? 'opacity-40' : ''}`}
                                >
                                    {originalMethodInfo && (
                                        <originalMethodInfo.Icon className="text-white" size={18} />
                                    )}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <span className={`block font-bold text-sm ${originalInAvailable ? 'text-gray-800' : 'text-gray-400'}`}>
                                        {originalMethodInfo?.label || originalRaw || '—'}
                                    </span>
                                    {!originalInAvailable && (
                                        <span className="text-[10px] text-gray-400">
                                            Troca obrigatória para este método
                                        </span>
                                    )}
                                </div>

                                {selectedMethod === originalNorm && (
                                    <FaCheckCircle className="text-custom_blue shrink-0" size={18} />
                                )}
                            </button>
                        </div>

                        {/* PIX→PIX info */}
                        {selectedMethod === 'PIX' && originalNorm === 'PIX' && (
                            <div className="flex items-start gap-2 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2.5">
                                <IoInformationCircleOutline className="text-teal-500 mt-0.5 shrink-0" size={15} />
                                <p className="text-xs text-teal-700 font-medium leading-snug">
                                    Confirmação <span className="font-bold">manual via PIX</span> — confirma que o
                                    responsável efetuou o pagamento via PIX fora do fluxo automático.
                                </p>
                            </div>
                        )}

                        {/* ── Alternatives grid ── */}
                        {alternatives.length > 0 && (
                            <div>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="h-px bg-slate-200 flex-1" />
                                    <span className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">
                                        ou trocar para
                                    </span>
                                    <div className="h-px bg-slate-200 flex-1" />
                                </div>

                                <div className="grid grid-cols-3 gap-2">
                                    {alternatives.map(({ key, label, Icon, color }) => {
                                        const isSelected = selectedMethod === key;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => selectAlternative(key)}
                                                disabled={loading}
                                                className={`relative aspect-[5/3] rounded-xl overflow-hidden transition-all duration-200 disabled:cursor-not-allowed
                                                    ${isSelected
                                                        ? 'ring-4 ring-custom_blue shadow-md scale-[1.04]'
                                                        : 'ring-1 ring-slate-200 hover:ring-slate-300 hover:shadow-sm'}`}
                                            >
                                                <div className={`absolute inset-0 ${color}`} />
                                                <Icon className="absolute -right-3 -bottom-3 text-white opacity-10 text-[72px]" />
                                                {isSelected && (
                                                    <div className="absolute top-1.5 right-1.5 bg-white text-custom_blue rounded-full">
                                                        <FaCheckCircle size={13} />
                                                    </div>
                                                )}
                                                <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-10 p-1">
                                                    <Icon size={20} className="mb-1" />
                                                    <span className="font-bold text-[10px] leading-tight text-center">{label}</span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* ── Brand selector (CREDIT or DEBIT only) ── */}
                        {needsBrand && (
                            <div className="animate-in fade-in slide-in-from-bottom-2 duration-200">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                                        Bandeira
                                    </label>
                                    {!brand && (
                                        <span className="text-[10px] text-red-500 font-bold animate-pulse flex items-center gap-0.5">
                                            <IoWarningOutline size={12} /> obrigatória
                                        </span>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {CARD_BRANDS.map(b => (
                                        <button
                                            key={b}
                                            type="button"
                                            onClick={() => setBrand(prev => prev === b ? null : b)}
                                            disabled={loading}
                                            className={`px-4 py-2 rounded-full text-xs font-bold transition-all disabled:cursor-not-allowed
                                                ${brand === b
                                                    ? 'bg-custom_blue text-white shadow-md'
                                                    : 'bg-white text-gray-600 border border-gray-200 hover:border-custom_blue hover:text-custom_blue'}`}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* FOODZCREDIT warning */}
                        {selectedMethod === 'FOODZCREDIT' && (
                            <div className="flex items-start gap-2 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2.5">
                                <IoWarningOutline className="text-orange-500 mt-0.5 shrink-0" size={15} />
                                <p className="text-xs text-orange-700 font-medium leading-snug">
                                    O saldo de <span className="font-bold">Crédito Foodz</span> do aluno será
                                    debitado automaticamente no valor do pedido.
                                </p>
                            </div>
                        )}

                        {/* Note — only when an alternative is chosen */}
                        {isAlternativeSelected && (
                            <div className="animate-in fade-in slide-in-from-bottom-2 duration-200">
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                                    Observação{' '}
                                    <span className="font-normal normal-case text-gray-400">(opcional)</span>
                                </label>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    disabled={loading}
                                    rows={2}
                                    maxLength={200}
                                    placeholder="Ex: Cliente pagou em dinheiro no balcão..."
                                    className="w-full p-3 border border-slate-200 rounded-lg text-sm text-gray-700 resize-none outline-none focus:ring-2 focus:ring-custom_blue focus:border-transparent disabled:opacity-50 transition-all"
                                />
                            </div>
                        )}
                    </div>

                    {/* ── Footer ── */}
                    <div className="px-6 pb-5 pt-3 flex gap-3 shrink-0 border-t border-slate-100">
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold text-gray-700 transition-colors text-sm disabled:opacity-50"
                        >
                            Voltar
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={loading || !canConfirm}
                            className="flex-1 bg-green-500 hover:bg-green-600 active:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg flex justify-center items-center gap-2 transition-colors text-sm shadow-sm"
                        >
                            {loading
                                ? <AiOutlineLoading className="animate-spin" size={17} />
                                : <><FaCheckCircle size={14} /> Confirmar Pagamento</>
                            }
                        </button>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};
